import { useId, useState, type ReactNode } from "react";

/**
 * Accessible hover/focus tooltip. Renders `children` as the trigger; `content` shows on
 * hover or keyboard focus. The trigger is keyboard-focusable and the bubble is wired up
 * via aria-describedby. Pure CSS positioning (no portal) — fine for our short tips.
 */
export default function Tooltip({
  content,
  children,
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();

  return (
    <span
      className={`tip${className ? ` ${className}` : ""}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
      role="button"
      aria-describedby={open ? id : undefined}
    >
      {children}
      <span
        id={id}
        role="tooltip"
        className={`tip-bubble${open ? " open" : ""}`}
        aria-hidden={!open}
      >
        {content}
      </span>
    </span>
  );
}

/**
 * A compact "(i)" info marker that opens a tooltip on hover/focus. Use next to a label or
 * metric to explain its meaning / target.
 */
export function InfoTip({ content }: { content: ReactNode }) {
  return (
    <Tooltip content={content} className="info-tip">
      <span className="info-tip-mark" aria-hidden>
        i
      </span>
      <span className="sr-only">More info</span>
    </Tooltip>
  );
}
